var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ProductComplianceLabelTypeEnum, ProductComplianceTechnicalHeadingTypeEnum } from '@inter-ikea-kompis/enums';
import { FontStyle } from '@inter-ikea-kompis/themes';
import { DexfSettingsUtility, EnergyClassParser, ProductUtility } from '@inter-ikea-kompis/utilities';
import { FocusOutline } from '@inter-ikea-kompis/component-focus-outline';
import { Hyperlink } from '@inter-ikea-kompis/component-hyperlink';
import { KeyboardInjectorFinder } from '@inter-ikea-kompis/component-keyboard';
import { Prompt as SkapaPrompt } from '@ingka/modal-webc/passive.js';
import { ModalFooter as SkapaModalFooter, ModalHeader as SkapaModalHeader, Sheet as SkapaSheet } from '@ingka/modal-webc/passive.js';
import EnergyEfficiencyClassMediaEnum from '../../enums/EnergyEfficiencyClassMediaEnum.js';
import EnergyEfficiencyClassModalEnum from '../../enums/EnergyEfficiencyClassModalEnum.js';
import EnergyEfficiencyNonRescaled from '../energy-efficiency-non-rescaled/EnergyEfficiencyNonRescaled.js';
import EnergyLabelCard from '../energy-label-card/EnergyLabelCard.js';
import EnergyEfficiencyClassStyle from './EnergyEfficiencyClassStyle.js';
/**
 * Energy Efficiency Class component.
 *
 * @event modalOpen Triggered when opening a modal
 * @event modalClose Triggered when closing a modal
 */
export default class EnergyEfficiencyClass extends KompisElement {
    constructor() {
        super(...arguments);
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The settings retrieved from settings service.
        this.settings = null;
        // Disables links. This attribute should be set to "true" when in a Kiosk.
        this.disableLinks = false;
        // Visible modal.
        this.visibleModal = null;
        this.sheetElement = null;
        this.dexfSettings = null;
    }
    /**
     * Renders the energy icons/flags.
     */
    render() {
        // We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslationsAndSettings()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        return this.getEnergyImages();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.closeSheet();
    }
    async connectedCallback() {
        this.customElements.define('skapa-sheet', SkapaSheet);
        this.customElements.define('skapa-modal-footer', SkapaModalFooter);
        this.customElements.define('skapa-modal-header', SkapaModalHeader);
        this.customElements.define('skapa-prompt', SkapaPrompt);
        this.customElements.define('kompis-hyperlink', Hyperlink);
        this.customElements.define('kompis-focus-outline', FocusOutline);
        this.customElements.define('kompis-energy-efficiency-non-rescaled', EnergyEfficiencyNonRescaled);
        this.customElements.define('kompis-energy-label-card', EnergyLabelCard);
        super.connectedCallback();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('localizedInformation') &&
            this.localizedInformation &&
            !this.settings) {
            this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
        }
        if (changedProperties.has('settings') && this.settings) {
            this.dexfSettings = this.settings;
        }
        if (changedProperties.has('visibleModal')) {
            if (this.visibleModal) {
                this.openSheet();
            }
            else {
                this.closeSheet();
            }
        }
        return true;
    }
    hasTranslationsAndSettings() {
        return (!!this.translations && !!this.settings) || !!this.localizedInformation;
    }
    getTranslations() {
        const translations = this.translations
            ? this.translations
            : this.localizedInformation?.translations;
        return translations;
    }
    getSettings() {
        return this.dexfSettings;
    }
    getEnergyImages() {
        const imageUrls = this.getImageUrls();
        let renderResult;
        if (imageUrls != null && imageUrls.length) {
            renderResult = this.showEnergyImages(imageUrls);
        }
        // Use non rescaled energy class for EU - fallback if complianceTechnical is missing and non rescaled should be shown
        else if (this.getSettings().kompis.localization.useNonRescaledEnergyLabel) {
            renderResult = this.showLegacyEuEnergyNonRescaledImage();
        }
        return renderResult
            ? renderResult
            : html `
					<style>
						:host {
							display: none;
						}
					</style>
				`;
    }
    /**
     * Extract image URL from product.
     */
    getImageUrls() {
        if (this.product.content?.complianceTechnical?.length) {
            const energyEfficiencyClasses = this.product.content.complianceTechnical.filter((complianceTechnicalItem) => complianceTechnicalItem.headingType ===
                ProductComplianceTechnicalHeadingTypeEnum.energyEfficiencyClass ||
                (complianceTechnicalItem.headingType ===
                    ProductComplianceTechnicalHeadingTypeEnum.hyperlink &&
                    complianceTechnicalItem.text !== 'SEC'));
            const labelsUrls = [];
            energyEfficiencyClasses.forEach((energyClass) => {
                const find = (fileTypeCode) => {
                    return (energyClass.media?.find((mediaType) => mediaType.fileTypeCode === fileTypeCode)?.url ??
                        null);
                };
                const url = find(EnergyEfficiencyClassMediaEnum.svg) ?? find(EnergyEfficiencyClassMediaEnum.jpeg);
                if (url != null) {
                    labelsUrls.push(url);
                }
            });
            return labelsUrls;
        }
        return null;
    }
    /**
     * Fetches the energy images.
     *
     * @param imageUrls ...
     */
    showEnergyImages(imageUrls) {
        return html `
			<style>
				${EnergyEfficiencyClassStyle.getStyle({
            energyType: ProductUtility.getProductComplianceType(this.product)
        })}
			</style>
			${imageUrls.map((imageUrl) => html `
					<kompis-focus-outline .theme="${this.theme}">
						<div
							class="imageContainer"
							tabindex="0"
							role="button"
							@keydown="${(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                this.sendModalOpen();
            }
        }}"
							@click="${(event) => {
            event.preventDefault();
            event.stopPropagation();
            this.sendModalOpen();
        }}"
						>
							<img alt="${this.getImageAlt()}" src="${imageUrl}" />
						</div>
					</kompis-focus-outline>
				`)}
		`;
    }
    /**
     * Fetches the non rescaled energy image (EU) if the svg is not provided with the product data.
     * Exists as a fallback if the data is not available but should show the non rescaled flag.
     */
    showLegacyEuEnergyNonRescaledImage() {
        if (EnergyClassParser.parseProduct(this.product)) {
            return html ` <kompis-focus-outline .theme="${this.theme}">
				<kompis-energy-efficiency-non-rescaled
					tabindex="0"
					@keydown="${(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    this.sendModalOpen();
                }
            }}"
					@click="${(event) => {
                event.preventDefault();
                event.stopPropagation();
                this.sendModalOpen();
            }}"
					.product="${this.product}"
					.theme="${this.theme}"
				></kompis-energy-efficiency-non-rescaled>
			</kompis-focus-outline>`;
        }
        return null;
    }
    sendModalOpen() {
        this.dispatchEvent(new CustomEvent('modalOpen', {
            bubbles: true,
            detail: {
                visibleModal: EnergyEfficiencyClassModalEnum.energyLabelCard,
                triggerElement: this.getTriggerElement()
            }
        }));
    }
    getTriggerElement() {
        return ((this.shadowRoot?.querySelector('.imageContainer') ??
            this.shadowRoot?.querySelector(this.customElements.getInternalName('kompis-energy-efficiency-non-rescaled'))));
    }
    closeSheet() {
        if (this.sheetElement) {
            this.sheetElement.open = false;
            this.sheetElement = null;
        }
    }
    openSheet() {
        if (this.sheetElement) {
            return;
        }
        const sheetElement = this.createSheetElement();
        this.sheetElement = sheetElement;
        const parent = KeyboardInjectorFinder.findKeyboardInjectorElement() || document.body;
        parent.appendChild(sheetElement);
        sheetElement.open = true;
    }
    createSheetElement() {
        const sheetElement = this.createSheet();
        const sheetHeaderElement = this.createSheetHeader();
        const cardElement = this.createCard();
        sheetElement.appendChild(sheetHeaderElement);
        sheetElement.appendChild(cardElement);
        return sheetElement;
    }
    createSheet() {
        const sheetElement = this.createInternalElement('skapa-sheet');
        sheetElement.size = 'small';
        sheetElement.fullHeight = true;
        sheetElement.style.fontFamily = FontStyle.getFontFamily(this.theme);
        sheetElement.style.zIndex = '500';
        sheetElement.addEventListener('closerequest', () => {
            if (this.sheetElement) {
                this.dispatchEvent(new CustomEvent('modalClose', {
                    bubbles: true,
                    detail: {
                        visibleModal: null,
                        triggerElement: this.getTriggerElement()
                    }
                }));
            }
        });
        sheetElement.addEventListener('closed', () => {
            sheetElement.parentElement?.removeChild(sheetElement);
        });
        return sheetElement;
    }
    createSheetHeader() {
        const sheetHeaderElement = this.createInternalElement('skapa-modal-header');
        sheetHeaderElement.slot = 'header';
        const headerSpan = document.createElement('span');
        headerSpan.slot = 'closebutton-label';
        headerSpan.innerText = this.getTranslations().modalAriaLabelButtonClose;
        sheetHeaderElement.appendChild(headerSpan);
        return sheetHeaderElement;
    }
    createCard() {
        const cardElement = this.createInternalElement('kompis-energy-label-card');
        cardElement.theme = this.theme;
        cardElement.product = this.product;
        const labelTypes = [
            ProductComplianceLabelTypeEnum.euEnergy,
            ProductComplianceLabelTypeEnum.usEnergy,
            ProductComplianceLabelTypeEnum.chileEnergyCompliance
        ];
        cardElement.labelFilter =
            this.product.content?.complianceLabel?.filter((label) => labelTypes.includes(label.labelType)) ?? null;
        cardElement.localizedInformation = this.localizedInformation;
        cardElement.translations = this.translations;
        if (this.disableLinks) {
            cardElement.disableLinks = true;
        }
        return cardElement;
    }
    /**
     * Returns corresponding alt text for the energy efficiency class image.
     */
    getImageAlt() {
        const complianceLabelType = ProductUtility.getProductComplianceType(this.product);
        switch (complianceLabelType) {
            case ProductComplianceTechnicalHeadingTypeEnum.energyEfficiencyClass: {
                const eecObject = this.product.content?.complianceTechnical?.find((obj) => obj.headingType === 'ENERGY_EFFICIENCY_CLASS');
                if (!eecObject) {
                    return '';
                }
                const { headingText, text } = eecObject;
                return `${headingText} ${text}`;
            }
            case ProductComplianceTechnicalHeadingTypeEnum.hyperlink: {
                return this.product.content?.complianceLabel?.find((obj) => obj.labelType)?.typeName ?? '';
            }
            case ProductComplianceTechnicalHeadingTypeEnum.nonRescaled: {
                const eecObject = this.product.content?.technicalInformation?.find((obj) => obj.typeCode === '00026');
                if (!eecObject) {
                    return '';
                }
                const { typeName, valueMetric } = eecObject;
                return `${typeName} ${valueMetric}`;
            }
            default: {
                return '';
            }
        }
    }
}
__decorate([
    property({ type: Object, required: true })
], EnergyEfficiencyClass.prototype, "product", void 0);
__decorate([
    property({ type: Object, required: true })
], EnergyEfficiencyClass.prototype, "theme", void 0);
__decorate([
    property({ type: Object })
], EnergyEfficiencyClass.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], EnergyEfficiencyClass.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], EnergyEfficiencyClass.prototype, "settings", void 0);
__decorate([
    property({ type: Boolean })
], EnergyEfficiencyClass.prototype, "disableLinks", void 0);
__decorate([
    property({ type: String })
], EnergyEfficiencyClass.prototype, "visibleModal", void 0);
//# sourceMappingURL=EnergyEfficiencyClass.js.map